import React, { useState, useEffect, useCallback } from 'react';
import {
    Star,
    CheckCircle,
    XCircle,
    Trash2,
    RefreshCw,
    Filter,
    MessageSquare,
    Clock,
    ThumbsUp,
    Reply,
    X,
    Save,
} from 'lucide-react';
import Notification from '../Common/Notification';
import { storefrontGet, storefrontPut, storefrontDelete } from '../../services/storefrontApi';

const STATUS_OPTIONS = [
    { key: '',         label: 'Tous' },
    { key: 'pending',  label: 'En attente' },
    { key: 'approved', label: 'Approuvés' },
    { key: 'rejected', label: 'Rejetés' },
];

const Stars = ({ rating, size = 14 }) => (
    <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(s => (
            <Star
                key={s}
                size={size}
                className={s <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
            />
        ))}
    </div>
);

const StatusBadge = ({ status }) => {
    const map = {
        pending:  { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
        approved: { label: 'Approuvé',   cls: 'bg-green-100 text-green-700' },
        rejected: { label: 'Rejeté',     cls: 'bg-red-100 text-red-700' },
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
};

/* ─── Reply modal ───────────────────────────────────────────────────────── */
const ReplyModal = ({ review, onClose, onSave, isSaving }) => {
    const [reply, setReply] = useState(review?.admin_reply || '');
    const [verified, setVerified] = useState(review?.verified ?? false);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Répondre à l'avis</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>
                <div className="px-5 py-5 space-y-4">
                    {/* Original review */}
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Stars rating={review.rating} />
                            <span className="font-medium text-sm text-gray-800 dark:text-white">{review.author_name}</span>
                        </div>
                        {review.title && <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{review.title}</p>}
                        <p className="text-sm text-gray-600 dark:text-gray-300">{review.comment}</p>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Votre réponse</label>
                        <textarea
                            value={reply}
                            onChange={e => setReply(e.target.value)}
                            rows={4}
                            placeholder="Merci pour votre avis..."
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700 dark:text-gray-200">Achat vérifié</span>
                        <button
                            type="button"
                            onClick={() => setVerified(v => !v)}
                            className={`relative h-6 w-11 rounded-full transition-colors ${verified ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                        >
                            <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${verified ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>
                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave({ admin_reply: reply, verified })}
                        disabled={isSaving}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        Enregistrer la réponse
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Review card ───────────────────────────────────────────────────────── */
const ReviewCard = ({ review, onApprove, onReject, onDelete, onReply }) => {
    const date = review.created_at
        ? new Date(review.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
        : '';

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Stars rating={review.rating} />
                        <span className="font-semibold text-sm text-gray-800 dark:text-white">{review.author_name}</span>
                        {review.verified && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                <ThumbsUp size={10} /> Achat vérifié
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={review.status} />
                        {date && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={10} />{date}</span>}
                    </div>
                </div>
                <button onClick={() => onDelete(review)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0">
                    <Trash2 size={14} />
                </button>
            </div>

            {review.title && <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{review.title}</p>}
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{review.comment}</p>

            {review.admin_reply && (
                <div className="mt-3 pl-3 border-l-2 border-primary">
                    <p className="text-xs font-medium text-primary mb-1">Votre réponse</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{review.admin_reply}</p>
                </div>
            )}

            <div className="flex gap-2 mt-4 flex-wrap">
                {review.status !== 'approved' && (
                    <button
                        onClick={() => onApprove(review)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    >
                        <CheckCircle size={12} /> Approuver
                    </button>
                )}
                {review.status !== 'rejected' && (
                    <button
                        onClick={() => onReject(review)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                        <XCircle size={12} /> Rejeter
                    </button>
                )}
                <button
                    onClick={() => onReply(review)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors"
                >
                    <Reply size={12} /> {review.admin_reply ? 'Modifier la réponse' : 'Répondre'}
                </button>
            </div>
        </div>
    );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const ReviewsSettings = () => {
    const [filterStatus, setFilterStatus] = useState('pending');
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [replyTarget, setReplyTarget] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type !== 'loading') setTimeout(() => setNotification(n => ({ ...n, show: false })), 3000);
    };

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = { page: 1, limit: 50 };
            if (filterStatus) params.status = filterStatus;
            const res = await storefrontGet('/reviews', params);
            const data = res.data?.data ?? res.data ?? [];
            setReviews(Array.isArray(data) ? data : []);
        } catch {
            showNotification('error', 'Impossible de charger les avis.');
        } finally {
            setIsLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (review, status) => {
        try {
            await storefrontPut(`/reviews/${review.id}`, { status });
            setReviews(prev => prev.map(r => r.id === review.id ? { ...r, status } : r));
            showNotification('success', `Avis ${status === 'approved' ? 'approuvé' : 'rejeté'}.`);
        } catch {
            showNotification('error', 'Erreur lors de la mise à jour.');
        }
    };

    const handleDelete = async (review) => {
        if (!window.confirm('Supprimer cet avis ?')) return;
        try {
            await storefrontDelete(`/reviews/${review.id}`);
            setReviews(prev => prev.filter(r => r.id !== review.id));
            showNotification('success', 'Avis supprimé.');
        } catch {
            showNotification('error', 'Impossible de supprimer cet avis.');
        }
    };

    const handleSaveReply = async (data) => {
        setIsSaving(true);
        try {
            await storefrontPut(`/reviews/${replyTarget.id}`, data);
            setReviews(prev => prev.map(r => r.id === replyTarget.id ? { ...r, ...data } : r));
            showNotification('success', 'Réponse enregistrée.');
            setReplyTarget(null);
        } catch {
            showNotification('error', 'Erreur lors de l\'enregistrement.');
        } finally {
            setIsSaving(false);
        }
    };

    // Stats
    const stats = {
        total:    reviews.length,
        pending:  reviews.filter(r => r.status === 'pending').length,
        approved: reviews.filter(r => r.status === 'approved').length,
        avg:      reviews.length
            ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
            : '—',
    };

    return (
        <div className="px-4 py-6 max-w-4xl mx-auto">
            <Notification show={notification.show} type={notification.type} message={notification.message} onClose={() => setNotification(n => ({ ...n, show: false }))} />

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                    { label: 'Total',        value: stats.total,    cls: 'text-gray-800 dark:text-white' },
                    { label: 'En attente',   value: stats.pending,  cls: 'text-amber-600' },
                    { label: 'Approuvés',    value: stats.approved, cls: 'text-green-600' },
                    { label: 'Note moyenne', value: stats.avg,      cls: 'text-primary', icon: <Star size={14} className="fill-amber-400 text-amber-400" /> },
                ].map(stat => (
                    <div key={stat.label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{stat.label}</p>
                        <div className={`flex items-center gap-1.5 text-2xl font-bold ${stat.cls}`}>
                            {stat.icon}
                            {stat.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2 mb-5 flex-wrap">
                <Filter size={15} className="text-gray-400" />
                {STATUS_OPTIONS.map(o => (
                    <button
                        key={o.key}
                        onClick={() => setFilterStatus(o.key)}
                        className={`px-3 py-1.5 text-sm rounded-full transition-colors ${filterStatus === o.key ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <RefreshCw size={20} className="animate-spin text-primary" />
                </div>
            ) : reviews.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <MessageSquare size={40} className="mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Aucun avis {filterStatus ? `avec le statut « ${STATUS_OPTIONS.find(o => o.key === filterStatus)?.label} »` : ''}.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {reviews.map(r => (
                        <ReviewCard
                            key={r.id}
                            review={r}
                            onApprove={rev => updateStatus(rev, 'approved')}
                            onReject={rev => updateStatus(rev, 'rejected')}
                            onDelete={handleDelete}
                            onReply={setReplyTarget}
                        />
                    ))}
                </div>
            )}

            {replyTarget && (
                <ReplyModal
                    review={replyTarget}
                    onClose={() => setReplyTarget(null)}
                    onSave={handleSaveReply}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default ReviewsSettings;
